import { FeatureFlag } from '@ps/web-core-enums';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import type { RootState } from '@swf/core/store';
import type { CorrectScoreTemplateConfig } from '@swf/generated/types/api/sports';
import { Selection } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  MarketTemplateComponent,
  SingleMarketTemplateProps
} from '@swf/sports/types/templates';

import Counter from '../../components/Counter/Counter';
import type { Score } from '../../types/correctScore';
import { useScoreInputRanges } from '../../utils/useScoreInputRanges';
import Grid from '../Grid/Grid';

import styles from './CorrectScore.scss';

const getScoreFromRunnerName = (runnerName: string) => {
  const match = runnerName.match(
    /(?<participant>\w.+?)?\s*(?<homeScore>\d+)\s*-\s*(?<awayScore>\d+)/i
  );
  const { awayScore, homeScore } = match?.groups ?? {};
  return [Number(homeScore), Number(awayScore)];
};

const CorrectScore: MarketTemplateComponent<SingleMarketTemplateProps> = ({
  marketId,
  templateConfig
}) => {
  const config = templateConfig as CorrectScoreTemplateConfig;
  const isCorrectScoreTemplateEnabled = useFeature(FeatureFlag.ISPEnableCorrectScoreTemplate);
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const market = useSelector((state: RootState) => widgetSelect.market(state, marketId));
  const { eventStartTime, participants } = useSelector((state: RootState) =>
    widgetSelect.event(state, market?.eventId ?? -1)
  );

  const runners = useMemo(
    () => market?.runners.filter((r) => config.runners.includes(r.selectionId)) ?? [],
    [config.runners, market]
  );

  const anyOtherScoresRunner = useMemo(
    () => market?.runners.find((r) => config.anyOtherScoresRunners.includes(r.selectionId)),
    [config.anyOtherScoresRunners, market?.runners]
  );

  const orderedScores = useMemo(
    () =>
      runners
        .map<[homeScore: number, awayScore: number, totalGoals: number]>(({ runnerName }) => {
          const [homeScore, awayScore] = getScoreFromRunnerName(runnerName);
          return [homeScore, awayScore, homeScore + awayScore];
        })
        .sort(([, , totalGoalsA], [, , totalGoalsB]) => totalGoalsA - totalGoalsB)
        .map<Score>(([homeScore, awayScore]) => [homeScore, awayScore]),
    [runners]
  );

  const [score, setScore] = useState<Score>(orderedScores.at(0) ?? [0, 0]);

  const {
    homeRange: [homeMin, homeMax],
    awayRange: [awayMin, awayMax]
  } = useScoreInputRanges(orderedScores, score);

  const selectedRunner = market?.runners.find(({ runnerName }) => {
    const [homeScore, awayScore] = getScoreFromRunnerName(runnerName);
    return homeScore === score[0] && awayScore === score[1];
  });

  useEffect(() => {
    if (!selectedRunner) {
      setScore(orderedScores.at(0) ?? [0, 0]);
    }
  }, [orderedScores, selectedRunner]);

  if (!isCorrectScoreTemplateEnabled) {
    return <Grid marketId={marketId} templateConfig={templateConfig} />;
  }

  if (!participants || !market) return null;

  const onChange = (team: 'home' | 'away', score: number) => {
    setScore(([prevHome, prevAway]) => (team === 'home' ? [score, prevAway] : [prevHome, score]));
  };

  return (
    <div data-testid="market-template-correct-score">
      <div className={styles.participantsContainer}>
        <div className={styles.participantWrapper}>
          <Counter
            min={homeMin}
            max={homeMax}
            onChange={(score) => onChange('home', score)}
            label={participants[0]}
          />
        </div>

        <div className={styles.participantWrapper}>
          <Counter
            min={awayMin}
            max={awayMax}
            onChange={(score) => onChange('away', score)}
            label={participants[1]}
          />
        </div>
      </div>
      {selectedRunner ? (
        <div className={styles.buttonContainer}>
          <Selection
            key={selectedRunner.selectionId}
            competitionId={market.competitionId}
            eventId={market.eventId}
            eventTypeId={market.eventTypeId}
            handicap={selectedRunner.handicap}
            legTypes={market.legTypes}
            marketId={market.marketId}
            marketStatus={market.marketStatus}
            marketType={market.marketType}
            odds={selectedRunner.winRunnerOdds}
            runnerResultType={selectedRunner.resultType}
            runnerStatus={selectedRunner.runnerStatus}
            selectionId={selectedRunner.selectionId}
            startTime={eventStartTime}
          />
        </div>
      ) : null}
      {anyOtherScoresRunner ? (
        <div className={styles.allOtherScoreRow} data-testid="any-other-scores-runner">
          <div className={styles.allOtherScoreRunner}>{anyOtherScoresRunner.runnerName}</div>
          <div>
            <Selection
              competitionId={market.competitionId}
              eventId={market.eventId}
              eventTypeId={market.eventTypeId}
              handicap={anyOtherScoresRunner.handicap}
              legTypes={market.legTypes}
              marketId={market.marketId}
              marketStatus={market.marketStatus}
              marketType={market.marketType}
              odds={anyOtherScoresRunner.winRunnerOdds}
              runnerResultType={anyOtherScoresRunner.resultType}
              runnerStatus={anyOtherScoresRunner.runnerStatus}
              selectionId={anyOtherScoresRunner.selectionId}
              startTime={eventStartTime}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

CorrectScore.marketTemplateType = 'EVENT';

export default CorrectScore;
